import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { PersonEditorComponent } from '../../../people/person-editor/person-editor.component';
import { EmployeePay } from '../../../../models/payroll';
import { ModalService } from '../../../../services/modal.service';
import { PayrollService } from '../../../../services/payroll.service';
import { PeopleService } from '../../../../services/people.service';
import { SharedService } from '../../../../services/shared.service';
import { PersonValidationService } from '../../../../services/person-validation.service';
import { ModalStates } from '../../../../models/modal';
import { PayrollManagement } from '../../../../tools/payroll-management';
import { Select, Store } from '@ngxs/store';
import { mergeMap } from 'rxjs/operators';
import {
  ClientSelectionLoadOpenChangeRequests,
  ClientSelectionState
} from '../../../../state/ClientSelectionState/ClientSelection.state';
import { PersonEditorState } from '../../../../state/PersonEditorState/PersonEditor.state';

@Component({
  selector: 'person-editor-payroll-modal',
  templateUrl: './person-editor-payroll-modal.component.html',
  styleUrls: ['./person-editor-payroll-modal.component.scss'],
  standalone: false
})
export class PersonEditorPayrollModalComponent implements OnInit {
  @ViewChild('modalContent', { static: true })
  public modalContent: TemplateRef<any>;

  @ViewChild('editor', { static: true })
  public editor: PersonEditorComponent;

  @Select(PersonEditorState.getHeaderName)
  public headerName: Observable<string>;

  @Select(PersonEditorState.getSaveButtonText)
  public saveButtonText: Observable<string>;

  @Select(PersonEditorState.getCurrentTabStatus)
  public currentTabStatus: Observable<{ IsValid: boolean; IsDirty: boolean }>;

  @Input() employeePay: EmployeePay;
  @Input() clientCode: string;
  @Input() payDate: Date;
  @Input() periodEnd: Date;
  @Input() personId: number;
  @Input() seqNo: number;
  @Input() year: number;

  @Output() onCloseEditor: EventEmitter<any> = new EventEmitter();

  public mode: 'add' | 'edit' = 'add';
  public changesSaved: boolean;
  public editsSavedSub: Subscription;
  public closeEditorSub: Subscription;

  constructor(
    private modalService: ModalService,
    private payrollService: PayrollService,
    private payrollMgmt: PayrollManagement,
    private peopleService: PeopleService,
    private sharedService: SharedService,
    private validationService: PersonValidationService,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.modalService.showAlertModal(
      `Saving any changes to ${this.payrollMgmt.getEmployeeFullName(this.employeePay)} ` +
        `will reset their pays in this payroll`,
      ModalStates.Info
    );

    if (this.personId) {
      this.mode = 'edit';
    }

    this.editsSavedSub = this.sharedService.personEditsSaved
      .pipe(
        mergeMap(_ => {
          return this.store.dispatch(new ClientSelectionLoadOpenChangeRequests());
        })
      )
      .subscribe(() => {
        this.changesSaved = true;
        this._resetPaylines();
      });
  }

  ngOnDestroy(): void {
    this.editsSavedSub.unsubscribe();
    this.closeEditorSub.unsubscribe();
  }

  public closePersonEditor() {
    // if there are unsaved changes, pop the confirmation message
    this.store.selectOnce(PersonEditorState.getEmployeeStatuses).subscribe(statuses => {
      this.closeEditorSub = this.validationService.onEditorLeave(null, null).subscribe(leave => {
        // if this modal is closed and reopened,
        // unubscribe from sub so this only fires once
        this.closeEditorSub?.unsubscribe();
        if (!leave) {
          // user clicked 'stay on page'
          return;
        }
        // user has left the person editor

        // show alert message if changes had been saved on the person
        if (this.changesSaved) {
          this._showAlert();
        } else {
          // close person editor modal
          this.onCloseEditor.emit({
            refresh: this.changesSaved,
            validToPay: true
          });
        }
      });
    });
  }

  public onSaveClick() {
    this.changesSaved = true;
    this._resetPaylines();
    this.editor.onSaveClick();
  }

  private _showAlert() {
    this.store.selectOnce(PersonEditorState.getPerson).subscribe(person => {
      const name = `${person.FirstName}${person.MiddleName ? ' ' + person.MiddleName : ''} ${
        person.LastName
      }`;
      if (person.InvalidToPayDetails?.length) {
        // if changes made to the person render them invalid to pay,
        // warn the user that they will no longer see that person on the grid.
        this.modalService.showAlertModal(
          `Changes saved to ${name} make this person invalid to pay.\n
        ${person.InvalidToPayDetails?.map(x => x.Message).join('\n')}
        \nAll pays for ${name} have been removed from this payroll.`,
          ModalStates.Warning
        );
      } else {
        this.modalService.showAlertModal(`The pays for ${name} have been reset`, ModalStates.Info);
      }

      // close person editor modal
      this.onCloseEditor.emit({
        refresh: this.changesSaved,
        // select the person if they are still on the grid
        validToPay: !person.InvalidToPayDetails?.length
      });
    });
  }

  private _resetPaylines() {
    this.payrollService
      .deleteEmployeePaylines(this.clientCode, this.year, this.seqNo, this.employeePay.PersonId)
      .subscribe(() => {});
  }
}
